import type {
  ExperimentStatsWithPageInfo,
  ExperimentTestStats,
} from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Badge from '@superfunnel/ui-core/v2/Badge';
import { classNames } from '@superfunnel/utils';
import { Check } from 'iconoir-react';
import { useCallback, useMemo, useState } from 'react';

interface CompleteExperimentDialogProps {
  experiment: ExperimentStatsWithPageInfo;
  open: boolean;
  onClose: () => void;
  onComplete: (winnerTestId: string, winnerWebsiteId: string) => Promise<void>;
  isCompleting: boolean;
}

export function CompleteExperimentDialog({
  experiment,
  open,
  onClose,
  onComplete,
  isCompleting,
}: CompleteExperimentDialogProps) {
  const [selectedTestId, setSelectedTestId] = useState<string | null>(
    experiment.winnerId || null,
  );

  const handleComplete = useCallback(async () => {
    if (!selectedTestId) return;

    const winnerTest = experiment.tests.find(
      (t: ExperimentTestStats) => t.testId === selectedTestId,
    );
    if (!winnerTest) return;

    await onComplete(selectedTestId, winnerTest.websiteId);
  }, [selectedTestId, experiment.tests, onComplete]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isCompleting,
        onClick: onClose,
      },
      {
        title: isCompleting ? 'Completing...' : 'Complete Experiment',
        type: 'primary',
        loading: isCompleting,
        disabled: !selectedTestId || isCompleting,
        onClick: handleComplete,
      },
    ],
    [isCompleting, selectedTestId, onClose, handleComplete],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="lg"
      title="Complete Experiment"
    >
      <div className="mb-4 flex items-center gap-2">
        <p className="text-sm text-gray-500">
          Select the winning variant to complete this experiment. The selected
          variant will receive 100% of the traffic.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {experiment.tests.map((test: ExperimentTestStats, index: number) => {
          const variantName = test.isControl
            ? 'Control'
            : test.description ||
              `Experiment ${String.fromCharCode(65 + index - 1)}`;
          const isSelected = selectedTestId === test.testId;
          const isRecommended = test.testId === experiment.winnerId;

          return (
            <button
              key={test.testId}
              onClick={() => setSelectedTestId(test.testId)}
              className={classNames(
                'flex items-center justify-between rounded-lg border p-4 text-left transition-colors',
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={classNames(
                    'flex h-5 w-5 items-center justify-center rounded-full border-2',
                    isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300',
                  )}
                >
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {variantName}
                    </span>
                    {test.isControl && (
                      <Badge variant="purple" size="sm">
                        Control
                      </Badge>
                    )}
                    {isRecommended && (
                      <Badge variant="success" size="sm">
                        Recommended
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    {test.visitors.toLocaleString()} visitors •{' '}
                    {test.primaryMetric.rate.toFixed(1)}% conversion rate
                    {!test.isControl && (
                      <>
                        {' '}
                        • {Math.round(test.primaryMetric.probabilityToBeBest)}%
                        confidence
                      </>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
